import React, { useMemo } from 'react'
import { Search, Trash2, FileEdit, MoreHorizontal } from 'lucide-react'
import { Dropdown } from 'Common/Components/Dropdown'
import { Link } from 'react-router-dom'
import { Workflow } from '../types'
import Loading from 'Common/Components/Loading'
import TableContainer from 'Common/TableContainer'

interface WorkflowsListProps {
  workflows: Workflow[];
  onRunWorkflow: (workflowId: string) => void;
  onCancelWorkflow: (workflowId: string) => void;
  onDeleteWorkflow: (workflowId: string) => void;
  onEditWorkflow: (workflowId: string) => void;
  loading?: boolean;
}

const WorkflowsList: React.FC<WorkflowsListProps> = ({ workflows, loading, onRunWorkflow, onCancelWorkflow, onDeleteWorkflow, onEditWorkflow }) => {

  const columns = useMemo(() => [
    {
      header: "Workflows",
      accessorKey: "name",
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div className="flex items-center gap-2" title={cell.getValue()}>
          <div className="grow truncate">
            <h6 className="mb-1">
              <Link to="#!" className="name truncate block max-w-[300px]" title={cell.getValue()}>{cell.getValue()?.charAt(0).toUpperCase() + cell.getValue()?.slice(1)}</Link>
            </h6>
            <p className="text-slate-500 dark:text-zink-200 truncate block max-w-[300px]" title={cell.row.original.designation}>{cell.row.original.designation}</p>
          </div>
        </div>
      ),
    },
    {
      header: "Description",
      accessorKey: "description",
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div title={cell.getValue()} className="flex items-center gap-2">
          <div className="grow truncate">
            <h6 className="mb-1 truncate block max-w-[500px]">
              {cell?.getValue()}
            </h6>
          </div>
        </div>
      ),
    },
    {
      header: "Last Updated",
      accessorKey: "lastModificationTime",
      enableColumnFilter: false,
      cell: (cell: any) => {
        const lastModificationTime = cell?.getValue();
        const creationTime = cell?.row?.original?.creationTime;
        const displayTime = lastModificationTime || creationTime;
        return (
          <div className="flex items-center gap-2">
            <div className="grow">
              <h6 className="mb-1">
                {formatDateString(displayTime)}
              </h6>
            </div>
          </div>
        );
      },
    },
    {
      header: "Action",
      enableColumnFilter: false,
      enableSorting: true,
      cell: (cell: any) => (
        <td className="!overflow-visible !max-w-none !truncate-none">
          <Dropdown className="relative">
            <Dropdown.Trigger
              className="flex items-center justify-center size-[30px] p-0 text-slate-500 btn bg-slate-100 hover:text-white hover:bg-slate-600 focus:text-white focus:bg-slate-600 focus:ring focus:ring-slate-100 active:text-white active:bg-slate-600 active:ring active:ring-slate-100 dark:bg-slate-500/20 dark:text-slate-400 dark:hover:bg-slate-500 dark:hover:text-white dark:focus:bg-slate-500 dark:focus:text-white dark:active:bg-slate-500 dark:active:text-white dark:ring-slate-400/20"
              id="usersAction1"
            >
              <MoreHorizontal className="size-3" />
            </Dropdown.Trigger>
            <Dropdown.Content
              placement="right-end"
              className="absolute z-50 py-2 mt-1 ltr:text-left rtl:text-right top-[-150px] right-[100px] list-none bg-white rounded-md shadow-md min-w-[10rem] dark:bg-zink-600"
              aria-labelledby="usersAction1"
            >
              <li>
                <button
                  className="block w-full px-4 py-1.5 text-base transition-all duration-200 ease-linear text-slate-600 hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200 text-left"
                  onClick={() => onEditWorkflow(cell.row.original.id)}
                >
                  <FileEdit className="inline-block size-3 ltr:mr-1 rtl:ml-1" /> <span className="align-middle">Edit</span>
                </button>
              </li>
              <li>
                <Link
                  className="block px-4 py-1.5 text-base transition-all duration-200 ease-linear text-slate-600 hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200"
                  to="#!"
                  onClick={() => onCancelWorkflow(cell.row.original.id)}
                >
                  <FileEdit className="inline-block size-3 ltr:mr-1 rtl:ml-1" /> <span className="align-middle">cancel</span>
                </Link>
              </li>
              <li>
                <Link
                  className="block px-4 py-1.5 text-base transition-all duration-200 ease-linear text-slate-600 hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200"
                  to="#!"
                  onClick={() => onDeleteWorkflow(cell.row.original.id)}
                >
                  <Trash2 className="inline-block size-3 ltr:mr-1 rtl:ml-1" /> <span className="align-middle">Delete</span>
                </Link>
              </li>
            </Dropdown.Content>
          </Dropdown>
        </td>
      )
    },
  ], [onEditWorkflow, onDeleteWorkflow, onCancelWorkflow]);

  const formatDateString = (dateString: string): string => {
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, '0')}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getFullYear()}`;
  };

  if (loading) {
    return (
      <Loading size="sm" color="blue" isVisible={true} />
    );
  }

  return (
    <>
      <div className="!pt-2">
        {workflows.length > 0 ? (
          <TableContainer
            isPagination={true}
            columns={columns}
            data={workflows}
            customPageSize={10}
            divclassName="sm:h-[calc(100vh-300px)] h-[calc(100vh-360px)] xl:h-[calc(100vh-360px)] 2xl:h-[calc(100vh-300px)] min-h-[200px] max-h-[calc(100vh-248px)] overflow-auto"
            tableclassName="w-full border-separate table-custom border-spacing-y-1 whitespace-nowrap"
            theadclassName="text-left relative rounded-md bg-slate-200 dark:bg-zink-600 after:absolute ltr:after:border-l-2 rtl:after:border-r-2 ltr:after:left-0 rtl:after:right-0 after:top-0 after:bottom-0 after:border-transparent [&.active]:after:border-custom-500 [&.active]:bg-slate-100 dark:[&.active]:bg-zink-600"
            thclassName="px-4 py-2.5 first:pl-5 last:pr-5 font-semibold sticky top-0 bg-gray-100 z-10"
            tdclassName="cursor-pointer px-4 py-2.5 first:pl-5 last:pr-5"
            PaginationClassName="flex flex-col items-center 2xl:mt-8 xl:mt-8 mt-0 sm:mt-0 md:mt-0 md:flex-row"
            tbodyclassName=""
            trclassName="hover:bg-gray-100"
          />
        ) : (
          <div className="noresult">
            <div className="py-6 text-center">
              <Search className="size-6 mx-auto text-slate-500 fill-sky-100 dark:sky-500/20" />
              <h6 className="mt-2 mb-1">No workflow created yet. Move to Workflow Designer tab to start</h6>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default WorkflowsList
